import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CookiePreferences {
  strictlyNecessary: boolean
  performanceAnalytics: boolean
  functional: boolean
  advertisingTargeting: boolean
}

export interface CookieConsentState {
  hasConsented: boolean
  preferences: CookiePreferences
  showBanner: boolean
  showPreferences: boolean
  showCookiePolicy: boolean
}

interface CookieConsentActions {
  acceptAll: () => void
  rejectNonEssential: () => void
  updatePreferences: (preferences: Partial<CookiePreferences>) => void
  savePreferences: () => void
  showBannerAction: () => void
  hideBannerAction: () => void
  showPreferencesPanelAction: () => void
  hidePreferencesPanelAction: () => void
  showCookiePolicyAction: () => void
  hideCookiePolicyAction: () => void
  resetConsent: () => void
}

type CookieConsentStore = CookieConsentState & CookieConsentActions

const defaultPreferences: CookiePreferences = {
  strictlyNecessary: true, // Always true, cannot be disabled
  performanceAnalytics: false,
  functional: false,
  advertisingTargeting: false
}

const initialState: CookieConsentState = {
  hasConsented: false,
  preferences: defaultPreferences,
  showBanner: false,
  showPreferences: false,
  showCookiePolicy: false
}

export const useCookieConsentStore = create<CookieConsentStore>()(
  persist(
    (set) => ({
      ...initialState,

      acceptAll: () => {
        set({
          hasConsented: true,
          preferences: {
            strictlyNecessary: true,
            performanceAnalytics: true,
            functional: true,
            advertisingTargeting: true
          },
          showBanner: false,
          showPreferences: false
        })
      },

      rejectNonEssential: () => {
        set({
          hasConsented: true,
          preferences: defaultPreferences,
          showBanner: false,
          showPreferences: false
        })
      },

      updatePreferences: (newPreferences) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...newPreferences,
            strictlyNecessary: true // Always keep strictly necessary cookies enabled
          }
        }))
      },

      savePreferences: () => {
        set({
          hasConsented: true,
          showBanner: false,
          showPreferences: false
        })
      },

      showBannerAction: () => {
        set({ showBanner: true })
      },

      hideBannerAction: () => {
        set({ showBanner: false })
      },

      showPreferencesPanelAction: () => {
        set({ showPreferences: true })
      },

      hidePreferencesPanelAction: () => {
        set({ showPreferences: false })
      },

      showCookiePolicyAction: () => {
        set({ showCookiePolicy: true })
      },

      hideCookiePolicyAction: () => {
        set({ showCookiePolicy: false })
      },

      resetConsent: () => {
        set({
          hasConsented: false,
          preferences: defaultPreferences,
          showBanner: true,
          showPreferences: false,
          showCookiePolicy: false
        })
      }
    }),
    {
      name: 'cookie-consent',
      partialize: (state) => ({
        hasConsented: state.hasConsented,
        preferences: state.preferences
      })
    }
  )
)
